import { Injectable } from '@angular/core';
import groupBy from 'lodash/groupBy';
import isEmpty from 'lodash/isEmpty';
import { defer, forkJoin, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { CachingService } from '../caching.service';
import { ConfigService } from '../config.service';
import { FwLegacyDialogService as DialogService } from '../dialogs/dialog.service';
import { FileBrowserDialog, SuggestedFileOption } from './file-browser/file-browser-dialog.component';
import { CoreApiService, queryAll } from '../core-api.service';
import { ContextWithOption } from './gear-version-selector/gear-version-selector.component';
import { LoggingService } from '../logging.service';
import { NotificationService } from '../notification.service';
import { ProfileService } from '../profile/profile.service';
import { RunGearDialog, RunGearOptions } from './run-gear-dialog/run-gear-dialog.component';
import { SelectGearDialog, DialogResult as SelectGearResult } from './select-gear-dialog/select-gear-dialog.component';
import { createCachedResource } from '../shared/caching.utils';
import { getContainerType } from '../shared/container.utils';
import { RequestState } from '../shared/enums/request-state.enum';
import { Container } from '../shared/models/container.model';
import { GearRule } from '../shared/models/gear-rule.model';
import { Gear, GearPermissions, GearSeries } from '../shared/models/gear.model';
import { ActivatedRoute } from '@angular/router';

export interface SelectGearOptions {
  context?: ContextWithOption
  gearFilter?: (gear: Gear) => boolean
  gearRule?: GearRule
  replaceInvalidGear?: boolean
}

// TODO: is there a reason this can't be provided in root?
@Injectable({
  providedIn: 'root',
})
export class GearService extends CachingService {
  public reloadGear$ = new Subject<string>()
  private versions = {
    state: RequestState.Empty,
    subject: new ReplaySubject<Gear[]>(1),
    request: queryAll(this.coreApi.gears.query, { all_versions: true }),
  }

  readonly gearAccessEnabled = this.configService.isFeatureEnabled('gear_access');
  readonly versions$ = this.versions.subject.asObservable()
  readonly versionsByName$ = defer(() => this.getAllVersions())
    .pipe(map(gears => groupBy(gears, 'gear.name')))

  private invalidGears = createCachedResource(queryAll(this.coreApi.gears.query, { include_invalid: true, all_versions: true }));
  private gearSeries = createCachedResource(
    this.gearAccessEnabled ?
      this.versionsByName$.pipe(
        // TODO: Replace this with a single endpoint fetch when the list endpoint is created for FLYW-16378
        switchMap(versions => {
          const gearNames = Object.keys(versions);
          return forkJoin(gearNames.map(name => this.coreApi.gears.series.get(name)));
        })
      ) : of([])
  );

  constructor(
    private configService: ConfigService,
    private dialog: DialogService,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private activatedRoute: ActivatedRoute,
  ) {
    super();
  }

  getAllVersions({ refresh = false, projectId = null } = {}): Observable<Gear[]> {
    if (projectId) {
      return this.profileService.exhaustiveMode$.pipe(
        switchMap((exhaustive) => {
          const params = { all_versions: true, project_id: projectId };
          return queryAll(this.coreApi.gears.query, exhaustive ? { ...params, exhaustive: true } : params) as Observable<Gear[]>;
        })
      );
    }
    return this.getCacheableResource(this.versions, this.versions$, { refresh });
  }

  getLatestVersions({ refresh = false, projectId = null } = {}): Observable<Gear[]> {
    if (projectId) {
      return this.profileService.exhaustiveMode$.pipe(
        switchMap((exhaustive) => {
          const params = { all_versions: false, project_id: projectId };
          return queryAll(this.coreApi.gears.query, exhaustive ? { ...params, exhaustive: true } : params) as Observable<Gear[]>;
        })
      );
    }
    return this.getCacheableResource(this.versions, this.versions$, { refresh });
  }

  getGearsByName(name: string): Observable<Gear[]> {
    const params = {
      all_versions: true,
      filter: `gear.name="${name}"`,
    };
    return this.profileService.exhaustiveMode$.pipe(
      switchMap((exhaustive) => {
        const queryParams = exhaustive ? { ...params, exhaustive: true } : params;
        return queryAll(this.coreApi.gears.query, queryParams) as Observable<Gear[]>;
      })
    );
  }

  updateGearAccess(series: GearSeries, permissions: GearPermissions): Observable<GearSeries[]> {
    return forkJoin([
      this.coreApi.gears.series.put(series.name, series),
      this.coreApi.gears.permissions.put(series.name, permissions),
    ]).pipe(
      tap(() => {
        this.notifications.success('gearAccess.update.success');
      }),
      catchError((error) => {
        this.notifications.error('gearAccess.update.error');
        this.logger.error(error);
        throw error;
      }),
    );
  }

  runGear(options: RunGearOptions): Promise<void> {
    const { batchMode, containers, context, contextOptions, collectionId } = options;
    if (isEmpty(containers[0])) {
      return Promise.reject(new Error('No containers provided'));
    }
    return this.dialog.open(RunGearDialog, {
      data: {
        batchMode: Boolean(batchMode),
        collectionId: collectionId ?? this.activatedRoute.snapshot.queryParams.collectionId,
        containers,
        context,
        contextOptions,
      },
    }).afterClosed().toPromise() as Promise<void>;
  }

  getInvalidGears({ refresh = false } = {}): Observable<Gear[]> {
    if (refresh) {
      this.invalidGears.refresh$.next();
    }
    return this.invalidGears.result$.pipe(
      map(result => result.filter(gear => gear.gear.custom?.flywheel?.invalid || Boolean(gear.disabled))),
    );
  }

  getAllGearSeries({ refresh = false } = {}): Observable<GearSeries[]> {
    if (refresh) {
      this.gearSeries.refresh$.next();
    }
    return this.gearSeries.result$;
  }

  selectGear(options: SelectGearOptions): Promise<SelectGearResult> {
    return this.dialog.open(SelectGearDialog, {
      data: {
        context: options.context,
        gearFilter: options.gearFilter,
        gearRule: options.gearRule,
        replaceInvalidGear: options.replaceInvalidGear ?? false,
      },
    }).afterClosed().toPromise() as Promise<SelectGearResult>;
  }

  /**
   * Open a file browser for gear inputs.
   * @param {string} options.fileInputKey - key for the gear input
   * @param {Container} options.container - container to start browsing
   * @param {string} options.context - 'analysis' or 'option'
   * @param {string} options.gearId - ID of the gear to suggest inputs
   * @param {string} [options.collectionId] - ID of a collection
   * @param {boolean} [options.isOutput] - true if we're picking an output container
   * @return {Promise<any>} resolves with the selected file
   */
  openFileBrowser(options: { fileInputKey: string, container: Container, context: string, gearId: string, collectionId?: string, isOutput?: boolean }): Promise<SuggestedFileOption> {
    const containerType = getContainerType(options.container);
    return this.dialog.open(FileBrowserDialog, {
      data: {
        collectionId: options.collectionId,
        containerId: options.container._id,
        containerType: containerType,
        gearContext: options.context,
        gearId: options.gearId,
        inputType: options.fileInputKey,
        isOutput: Boolean(options.isOutput),
      },
      backdropClass: 'file-browser-bg',
      panelClass: 'file-browser-panel',
      position: {
        top: '30px',
      },
    }).afterClosed().toPromise() as Promise<SuggestedFileOption>;
  }
}
